import React, { useEffect, useState, useCallback, useMemo } from 'react';
import './DesignOrders.css';

const STORAGE_KEY = 'toasty_admin_key';

const parseCardName = (pathname) => {
  // cards/{orderCode}_{background}_{timestamp}.png or .psd
  const file = pathname.split('/').pop() || '';
  const ext = (file.match(/\.(png|psd)$/i)?.[1] || '').toLowerCase();
  const [orderCode, ...rest] = file.replace(/\.(png|psd)$/i, '').split('_');
  const timestamp = rest.pop();
  const background = rest.join('_');
  return { orderCode, background: background.replace(/_/g, ' '), timestamp, ext };
};

// The PNG and layered PSD for one order upload separately (the PSD is a
// best-effort, fire-and-forget addition — see psdExport.js), so group blobs
// by orderCode to show one tile per order with both files attached.
const groupCards = (cards) => {
  const groups = new Map();
  for (const card of cards) {
    const { orderCode, background, ext } = parseCardName(card.pathname);
    if (!orderCode || !ext) continue;
    if (!groups.has(orderCode)) {
      groups.set(orderCode, { orderCode, background, uploadedAt: card.uploadedAt, png: null, psd: null });
    }
    const g = groups.get(orderCode);
    if (ext === 'psd') g.psd = card;
    else g.png = card;
    if (!g.background && background) g.background = background;
    if (new Date(card.uploadedAt) > new Date(g.uploadedAt)) g.uploadedAt = card.uploadedAt;
  }
  return Array.from(groups.values()).sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
};

const DesignOrders = () => {
  const [key, setKey] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [keyInput, setKeyInput] = useState('');
  const [cards, setCards] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | loading | error | ready
  const [errorMsg, setErrorMsg] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const fetchCards = useCallback(async (adminKey, code) => {
    setStatus('loading');
    setErrorMsg('');
    try {
      const params = new URLSearchParams({ key: adminKey });
      if (code) params.set('code', code);
      const res = await fetch(`/api/list-cards?${params.toString()}`);
      if (res.status === 401) {
        localStorage.removeItem(STORAGE_KEY);
        setKey('');
        setErrorMsg('Invalid admin key.');
        setStatus('error');
        return;
      }
      if (res.status === 400) {
        setErrorMsg('That design code doesn’t look right — it should be 4–12 letters/numbers.');
        setStatus('error');
        return;
      }
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      setCards(data.cards || []);
      setStatus('ready');
    } catch (err) {
      setErrorMsg('Could not load designs. Please try again.');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (key) fetchCards(key, activeSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, fetchCards]);

  const handleSearch = (e) => {
    e.preventDefault();
    const code = searchInput.trim();
    setActiveSearch(code);
    fetchCards(key, code);
  };

  const clearSearch = () => {
    setSearchInput('');
    setActiveSearch('');
    fetchCards(key, '');
  };

  const orderGroups = useMemo(() => groupCards(cards), [cards]);

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    localStorage.setItem(STORAGE_KEY, keyInput.trim());
    setKey(keyInput.trim());
  };

  if (!key) {
    return (
      <div className="design-orders-page">
        <div className="design-orders-login">
          <h1>🍞 Design Orders</h1>
          <p>Enter your admin key to view submitted card designs.</p>
          <form onSubmit={handleUnlock}>
            <input
              type="password"
              value={keyInput}
              onChange={e => setKeyInput(e.target.value)}
              placeholder="Admin key"
              autoFocus
            />
            <button type="submit">Unlock</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="design-orders-page">
      <header className="design-orders-header">
        <h1>🍞 Design Orders</h1>
        <form className="design-orders-search" onSubmit={handleSearch}>
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Find by design code…"
          />
          <button type="submit" disabled={status === 'loading'}>Find</button>
          {activeSearch && (
            <button type="button" className="design-orders-clear" onClick={clearSearch} disabled={status === 'loading'}>
              Show all
            </button>
          )}
        </form>
        <button
          className="design-orders-refresh"
          onClick={() => fetchCards(key, activeSearch)}
          disabled={status === 'loading'}
        >
          {status === 'loading' ? 'Loading…' : '↻ Refresh'}
        </button>
      </header>

      {status === 'error' && <p className="design-orders-error">{errorMsg}</p>}
      {status === 'ready' && orderGroups.length === 0 && (
        <p className="design-orders-empty">
          {activeSearch ? `No design found for code "${activeSearch}".` : 'No designs submitted yet.'}
        </p>
      )}

      <div className="design-orders-grid">
        {orderGroups.map(group => (
          <div key={group.orderCode} className="design-order-card">
            <a href={(group.png || group.psd).url} target="_blank" rel="noopener noreferrer">
              {group.png
                ? <img src={group.png.url} alt={group.orderCode} loading="lazy" />
                : <div className="design-order-no-thumb">PSD only</div>}
            </a>
            <div className="design-order-meta">
              <div className="design-order-code">{group.orderCode}</div>
              <div className="design-order-bg">{group.background}</div>
              <div className="design-order-date">{new Date(group.uploadedAt).toLocaleString()}</div>
              {group.psd && (
                <a className="design-order-psd-link" href={group.psd.url} target="_blank" rel="noopener noreferrer">
                  🎨 Download PSD
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DesignOrders;
